import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageSquare, MapPin, Star, ArrowRight, Zap, Shield, Clock, CheckCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ServiceCard } from '../components/ServiceCard';
import { BookingForm } from '../components/BookingForm';
import { businessInfo, services, testimonials } from '../mock';

export const Home = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const handleBookService = (service) => {
    setSelectedService(service);
    setIsBookingOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1625231334168-35067f8853ed"
            alt="High Performance Vehicle"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900/95 to-blue-950/90"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(15,23,42,0.8)_100%)]"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-block mb-6">
                <div className="flex items-center space-x-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full backdrop-blur-sm">
                  <Zap className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-400 text-sm font-semibold">Professional Automotive Services</span>
                </div>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                <span className="block">Professional</span>
                <span className="block bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  Auto Locksmith Services
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
                Expert key cutting & programming and emergency locksmith services across Cambridgeshire. Available 24/7 for all your automotive locksmith needs.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-blue-500/50"
                >
                  <Phone className="w-5 h-5" />
                  <span>{businessInfo.phone}</span>
                </a>
                <a
                  href={`sms:${businessInfo.phone}`}
                  className="flex items-center justify-center space-x-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all hover:scale-105 border border-slate-700"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Send SMS</span>
                </a>
              </div>
            </div>

            {/* Services Showcase Image - Larger */}
            <div className="relative">
              <div className="absolute inset-0 bg-blue-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-slate-900/60 backdrop-blur-md p-4 rounded-3xl border border-slate-800 shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_key-cut-prog/artifacts/8dw07kz4_image.png"
                  alt="SM Engineering Services - Vehicle Diagnostics, Key Programming, Lockouts"
                  className="w-full rounded-2xl"
                />
              </div>
            </div>

            {/* Features - Below Image */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
              <div className="flex flex-col items-center space-y-2 p-4 bg-slate-900/50 backdrop-blur-sm rounded-xl border border-slate-800">
                <CheckCircle className="w-6 h-6 text-blue-400" />
                <span className="text-gray-300 text-sm text-center">Professional Key Programming</span>
              </div>
              <div className="flex flex-col items-center space-y-2 p-4 bg-slate-900/50 backdrop-blur-sm rounded-xl border border-slate-800">
                <CheckCircle className="w-6 h-6 text-blue-400" />
                <span className="text-gray-300 text-sm text-center">24/7 Emergency Service</span>
              </div>
              <div className="flex flex-col items-center space-y-2 p-4 bg-slate-900/50 backdrop-blur-sm rounded-xl border border-slate-800">
                <CheckCircle className="w-6 h-6 text-blue-400" />
                <span className="text-gray-300 text-sm text-center">Mobile Service Available</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex flex-col items-center space-y-2 text-gray-400">
            <span className="text-sm">Scroll to explore</span>
            <div className="w-6 h-10 border-2 border-gray-400 rounded-full p-1">
              <div className="w-1.5 h-3 bg-blue-400 rounded-full animate-bounce"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center space-x-4 p-6 bg-slate-800 rounded-xl">
              <div className="p-3 bg-blue-600 rounded-lg">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">24/7</h3>
                <p className="text-gray-400">Emergency Callout</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-6 bg-slate-800 rounded-xl">
              <div className="p-3 bg-blue-600 rounded-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Fully Insured</h3>
                <p className="text-gray-400">Professional Service</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 p-6 bg-slate-800 rounded-xl">
              <div className="p-3 bg-blue-600 rounded-lg">
                <Star className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">5-Star Rated</h3>
                <p className="text-gray-400">Trusted by Hundreds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Our Expert Services
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Professional automotive locksmith solutions for all your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Map Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Serving Cambridgeshire & Beyond
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Based in Ely, we provide mobile services across a wide area including:
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {['Cambridge', 'King\'s Lynn', 'Peterborough', 'Bury St Edmunds', 'Norwich', 'March'].map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300">{area}</span>
                  </div>
                ))}
              </div>
              <div className="p-6 bg-slate-800 rounded-xl border border-slate-700">
                <p className="text-gray-300 mb-4">
                  <strong className="text-white">Need service outside these areas?</strong>
                </p>
                <p className="text-gray-400 text-sm">
                  Contact us to discuss your location. We may be able to arrange a mobile visit to your area.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-blue-600 rounded-2xl blur-3xl opacity-20"></div>
              <div className="relative rounded-2xl shadow-2xl border border-slate-800 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_key-cut-prog/artifacts/sohy7z8f_Map%20image.jpeg"
                  alt="Service Coverage Area"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-400">
              Trusted by drivers across Cambridgeshire
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="p-6 bg-slate-900 rounded-xl border border-slate-800 hover:border-blue-600 transition-all">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                  <div className="text-xs text-blue-400 font-medium">{testimonial.service}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-950 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contact us today for expert automotive services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${businessInfo.phone}`}
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-blue-500/50"
            >
              <Phone className="w-5 h-5" />
              <span>Call {businessInfo.phone}</span>
            </a>
            <button
              onClick={() => handleBookService(services[0])}
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all hover:scale-105 border border-slate-700"
            >
              <span>Request Callback</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Booking Modal */}
      {selectedService && (
        <BookingForm
          service={selectedService}
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
        />
      )}
    </div>
  );
};

export default Home;
